import { useTranslation } from 'react-i18next';

import { useRoleDetailStore } from '../store/role-detail.store';
import { formatDateTime } from '../utils/format-date';
import { PermissionMatrixTable } from './PermissionMatrixTable';

interface RoleDetailViewProps {
  /** The Root reserved Role (`isReserved`) — its matrix is fixed at WRITE for display (PRD 16 §3.5). */
  isRoot?: boolean;
}

export function RoleDetailView({ isRoot = false }: RoleDetailViewProps) {
  const { t } = useTranslation('roles');
  const mode = useRoleDetailStore((s) => s.mode);
  const draft = useRoleDetailStore((s) => s.draft);
  const currentRoleId = useRoleDetailStore((s) => s.currentRoleId);
  const createdAt = useRoleDetailStore((s) => s.currentRoleCreatedAt);
  const updatedAt = useRoleDetailStore((s) => s.currentRoleUpdatedAt);
  const nameError = useRoleDetailStore((s) => s.nameError);
  const updateDraftName = useRoleDetailStore((s) => s.updateDraftName);
  const updateDraftPermission = useRoleDetailStore((s) => s.updateDraftPermission);

  if (!draft) return null;

  const readOnly = mode === 'view';
  const showId = mode !== 'create';
  // SHCT-633 — the two server-owned timestamps ride with the id: both are absent
  // on create (the row is not there yet) and read-only everywhere else.
  const showTimestamps = showId;

  return (
    <div className="card" style={{ padding: 24 }}>
      {showId && (
        <div
          className="form-field"
          style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}
        >
          <label style={{ minWidth: 120, color: 'var(--muted-foreground)', fontWeight: 600 }}>
            {t('detail.idLabel')}
          </label>
          <input
            type="text"
            value={currentRoleId ?? ''}
            readOnly
            className="form-field__input"
            style={{ width: 120, background: 'var(--muted)' }}
          />
        </div>
      )}

      <div
        className="form-field"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}
      >
        <label
          style={{
            minWidth: 120,
            color: 'var(--muted-foreground)',
            fontWeight: 600,
            paddingTop: 8,
          }}
        >
          <span style={{ color: 'var(--destructive)' }}>*</span>{' '}
          {t('detail.nameLabel').replace(/^\*\s*/, '')}
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            type="text"
            value={draft.name}
            readOnly={readOnly}
            maxLength={30}
            onChange={(e) => updateDraftName(e.target.value)}
            aria-invalid={nameError ? true : undefined}
            aria-describedby={nameError ? 'role-name-error' : undefined}
            className="form-field__input"
            // SHCT-634 — the original placeholder read "비어 있음", which was PRD 16 §2.1.2's
            // *description* of the create-mode initial state ("이름 필드는 빈 값") mistaken for
            // on-screen copy. It was removed rather than replaced because no spec defined guidance
            // text; QC then supplied the wording on the ticket, so it is back — edit modes only,
            // since a saved role always has a name and view mode is read-only.
            placeholder={readOnly ? undefined : t('detail.namePlaceholder')}
            pattern="[A-Z0-9_]{2,30}"
            title={t('errors.nameInvalid')}
            style={{
              width: 300,
              ...(readOnly ? { background: 'var(--muted)' } : {}),
              ...(nameError ? { borderColor: 'var(--destructive)' } : {}),
            }}
          />
          {nameError && (
            <p
              id="role-name-error"
              style={{ margin: 0, fontSize: 12, color: 'var(--destructive)' }}
            >
              {nameError}
            </p>
          )}
        </div>
      </div>

      <div className="form-field" style={{ display: 'flex', gap: 16 }}>
        <label
          style={{
            minWidth: 120,
            color: 'var(--muted-foreground)',
            fontWeight: 600,
            paddingTop: 8,
          }}
        >
          <span style={{ color: 'var(--destructive)' }}>*</span>{' '}
          {t('detail.permissionMapLabel').replace(/^\*\s*/, '')}
        </label>
        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <PermissionMatrixTable
            permissionMap={draft.permissionMap}
            readOnly={readOnly}
            forceWrite={isRoot}
            onChange={updateDraftPermission}
          />
        </div>
      </div>

      {/*
        SHCT-633 — 최초 생성 / 최근 업데이트. The server already returns both on
        `GET /admin/roles` and the list column has always shown them; only the
        detail render was missing. Same `formatDateTime` as the list so the two
        screens can never print the same instant differently.
      */}
      {showTimestamps && (
        <div style={{ display: 'flex', gap: 32, marginTop: 24, flexWrap: 'wrap' }}>
          {(
            [
              ['detail.createdAtLabel', createdAt],
              ['detail.updatedAtLabel', updatedAt],
            ] as const
          ).map(([labelKey, value]) => (
            <div
              key={labelKey}
              className="form-field"
              style={{ display: 'flex', alignItems: 'center', gap: 16, margin: 0 }}
            >
              <label
                style={{ minWidth: 120, color: 'var(--muted-foreground)', fontWeight: 600 }}
              >
                {t(labelKey)}
              </label>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatDateTime(value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
